import React, { useEffect, useMemo, useState } from 'react';
import {
    Row,
    Input,
    Label,
    Button,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    InputGroup,
    InputGroupAddon,
} from 'reactstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Colxx } from '../../../../../../components/common/CustomBootstrap';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { NotificationManager } from '../../../../../../components/common/react-notifications';
import { AxiosAPI, getGroupID } from '../../../../../../helpers/AxiosAPI';
import { postAPI_GuestMessagingReservation_Create } from '../../../../../../constants/APIs';
import countryCodes from '../../../../../../data/dial_code';

const formatDate = (date) => (date ? moment(date).format('YYYY-MM-DD') : null);
const RESERVATION_STATUS = [
    { label: 'Arrived', value: 'Arrived' },
    { label: 'Departed', value: 'Departed' },
    { label: 'Confirmed', value: 'Confirmed' },
    { label: 'Cancelled', value: 'Cancelled' },
]

export default function CreateReservation({ visible, toggle, onSuccess }) {
    const [arrivalDate, setArrivalDate] = useState(null);
    const [departureDate, setDepartureDate] = useState(null);
    const [firstName, setFirstName] = useState('');
    const [middleName, setMiddleName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [countryCode, setCountryCode] = useState('+64');
    const [rawPhone, setRawPhone] = useState('');
    const [numAdults, setNumAdults] = useState('');
    const [numChildren, setNumChildren] = useState('');
    const [roomNumber, setRoomNumber] = useState('');
    const [roomTypeName, setRoomTypeName] = useState('');
    const [reference, setReference] = useState('')
    const [bookingSource, setBookingSource] = useState('')
    const [reservationStatusPms, setReservationStatusPms] = useState('')
    const [error, setError] = useState({
        firstName: "",
        lastName: "",
        email: "",
        arrivalDate: "",
        departureDate: ""
    })

    const [isSaving, setIsSaving] = useState(false);

    const { propertyId, groupId: storedGroupId, userName } = useSelector(({ settings }) => ({
        propertyId: settings.propertyId,
        groupId: settings.groupId,
        userName: settings.userName,

    }));
    const groupId = useMemo(() => storedGroupId || getGroupID(), [storedGroupId]);

    const resetForm = () => {
        setArrivalDate(null);
        setDepartureDate(null);
        setFirstName('');
        setMiddleName('');
        setLastName('');
        setEmail('');
        setPhone('');
        setCountryCode('+64');
        setRawPhone('');
        setNumAdults('');
        setNumChildren('');
        setRoomNumber('');
        setRoomTypeName('');
        setReference('')
        setBookingSource('')
        setReservationStatusPms('')
    };

    useEffect(() => {
        if (!visible) resetForm();
    }, [visible]);

    const handleSave = async () => {
        let newErrors = {};
        if (!firstName?.trim()) newErrors.firstName = "First name is mandatory";
        if (!lastName?.trim()) newErrors.lastName = "Last name is mandatory";
        if (!email?.trim()) newErrors.email = "Email is mandatory";

        if (!arrivalDate) newErrors.arrivalDate = "Arrival date is mandatory";
        if (!departureDate) newErrors.departureDate = "Departure date is mandatory";

        // Departure must be greater than arrival
        if (arrivalDate && departureDate) {
            if (moment(departureDate).isBefore(arrivalDate)) {
                newErrors.departureDate = "Departure date cannot be before arrival date";
            }
        }

        setError(newErrors);

        if (Object.keys(newErrors).length > 0) {
            return;
        }

        if (!propertyId || !groupId) {
            NotificationManager.error('Select a property to continue', 'Error', 3000);
            return;
        }

        const payload = {
            propertyId,
            groupId,
            PropertyCode: '',
            ChainCode: '',
            PmsReservationId: '',
            PmsConfirmationCode: '',
            ArrivalDate: formatDate(arrivalDate),
            DepartureDate: formatDate(departureDate),
            ArrivalEstimatedTime: arrivalDate ? moment(arrivalDate).format('HH:mm') : '',
            DepartureEstimatedTime: departureDate ? moment(departureDate).format('HH:mm') : '',
            NumberOfAdults: Number(numAdults) || 1,
            NumberOfChildren: Number(numChildren) || 0,
            RoomTypeCode: '',

            RoomNumber: roomNumber,
            RoomTypeName: roomTypeName,
            Reference: reference,
            bookingSourceName: bookingSource,
            CompanyName: '',
            ReservationStatusPms: reservationStatusPms,
            ReservationStatusHtng: '',
            GroupCode: '',
            CurrencyCode: '',
            IsPostingAllowed: true,
            CreatedBy: userName || 'system',
            DataSource: '1',
            NameFirst: firstName,
            NameMiddle: middleName,
            NameLast: lastName,
            PhoneMobile: phone,
            EmailIsPrimary: true,
            EmailAddress: email,
        };

        setIsSaving(true);
        try {
            await AxiosAPI({
                method: 'post',
                url: postAPI_GuestMessagingReservation_Create,
                data: payload,
            });
            NotificationManager.success('Reservation created successfully', 'Success', 3000);
            toggle();
            resetForm();
            if (onSuccess) onSuccess();
        } catch (err) {
            NotificationManager.error('Failed to create reservation', 'Error', 3000);
        } finally {
            setIsSaving(false);
        }
    };

    const numberOptions = (min, max) => {
        const option = [<option key="default" value="">Select</option>];
        for (let i = min; i <= max; i += 1) {
            option.push(
                <option key={i} value={Number(i)}>
                    {i}
                </option>
            );
        }
        return option;
    };

    return (
        <Modal isOpen={visible} toggle={toggle} wrapClassName="modal-right" backdrop="static">
            <ModalHeader toggle={toggle}>Create Reservation</ModalHeader>
            <ModalBody>
                <Row>
                    <Colxx sm={12}>
                        <h6 className="mt-1 mb-2"><b>Reservation Details</b></h6>
                    </Colxx>
                    <Colxx sm={6}>
                        <Label className="mt-2">Arrival Date <span style={{ color: "red" }}>*</span></Label>
                        <DatePicker
                            selected={arrivalDate}
                            onChange={(d) => {
                                setArrivalDate(d);
                                if (error.arrivalDate) setError({ ...error, arrivalDate: "" });
                            }}
                            placeholderText="arrival date"
                            dateFormat="dd/MM/yyyy"
                        />
                        {error.arrivalDate && (
                            <span style={{ color: "red", fontSize: "12px" }}>{error.arrivalDate}</span>
                        )}
                    </Colxx>

                    <Colxx sm={6}>
                        <Label className="mt-2">Departure Date <span style={{ color: "red" }}>*</span></Label>
                        <DatePicker
                            selected={departureDate}
                            onChange={(d) => {
                                setDepartureDate(d);
                                if (error.departureDate) setError({ ...error, departureDate: "" });
                            }}
                            placeholderText="departure date"
                            dateFormat="dd/MM/yyyy"
                        />
                        {error.departureDate && (
                            <span style={{ color: "red", fontSize: "12px" }}>{error.departureDate}</span>
                        )}
                    </Colxx>

                    <Colxx sm={6}>
                        <Label className="mt-2">Number of adults</Label>
                        <Input className='input_sty' type="select" value={numAdults} onChange={(e) => setNumAdults(e.target.value)}>
                            {numberOptions(1, 6)}
                        </Input>
                    </Colxx>
                    <Colxx sm={6}>
                        <Label className="mt-2">Number of children</Label>
                        <Input className='input_sty' type="select" value={numChildren} onChange={(e) => setNumChildren(e.target.value)}>
                            {numberOptions(0, 6)}
                        </Input>
                    </Colxx>

                    <Colxx sm={12}>
                        <Label className="mt-2">Room type</Label>
                        <Input className='input_sty' type="text" value={roomTypeName} onChange={(e) => setRoomTypeName(e.target.value)} placeholder="Room type" />
                    </Colxx>

                    <Colxx sm={12}>
                        <Label className="mt-2">Room number</Label>
                        <Input className='input_sty' type="text" value={roomNumber} onChange={(e) => setRoomNumber(e.target.value)} placeholder="Room number" />
                    </Colxx>

                    <Colxx sm={12}>
                        <Label className="mt-2">Reference Number</Label>
                        <Input className='input_sty' type="text" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Reference number" />
                    </Colxx>
                    <Colxx sm={12}>
                        <Label className="mt-2">Booking Source Name</Label>
                        <Input className='input_sty' type="text" value={bookingSource} onChange={(e) => setBookingSource(e.target.value)} placeholder="booking Source" />
                    </Colxx>
                    <Colxx sm={12}>
                        <Label className="mt-2">Reservation Status</Label>
                        <Input
                            type="select"
                            value={reservationStatusPms}
                            onChange={(e) => setReservationStatusPms(e.target.value)}
                        >
                            <option value="">Select Status</option>
                            {RESERVATION_STATUS.map(status => (
                                <option key={status.value} value={status.value}>
                                    {status.label}
                                </option>
                            ))}
                        </Input>

                    </Colxx>
                    <Colxx sm={12} className="mt-3">
                        <h6 className="mb-2"><b>Guest Details</b></h6>
                    </Colxx>
                    <Colxx sm={12}>
                        <Label className="mt-2">First Name <span style={{ color: "red" }}>*</span></Label>
                        <Input className='input_sty' type="text" value={firstName}
                            onChange={(e) => {
                                setFirstName(e.target.value);
                                if (error.firstName) setError({ ...error, firstName: "" });
                            }}
                            placeholder="First name"
                        />
                        {error.firstName && (
                            <span style={{ color: "red", fontSize: "12px" }}>{error.firstName}</span>
                        )}

                    </Colxx>
                    <Colxx sm={12}>
                        <Label className="mt-2">Middle Name (Optional)</Label>
                        <Input className='input_sty' type="text" value={middleName} onChange={(e) => setMiddleName(e.target.value)} placeholder="Middle name" />
                    </Colxx>
                    <Colxx sm={12}>
                        <Label className="mt-2">Last Name <span style={{ color: "red" }}>*</span></Label>
                        <Input
                            type="text"
                            value={lastName}
                            onChange={(e) => {
                                setLastName(e.target.value);
                                if (error.lastName) setError({ ...error, lastName: "" });
                            }}
                            placeholder="Last name"
                        />
                        {error.lastName && (
                            <span style={{ color: "red", fontSize: "12px" }}>{error.lastName}</span>
                        )}
                    </Colxx>
                    <Colxx sm={12}>
                        <Label className="mt-2">Email Address <span style={{ color: "red" }}>*</span></Label>
                        <Input
                            type="email"
                            value={email}
                            onChange={(e) => {
                                setEmail(e.target.value);
                                if (error.email) setError({ ...error, email: "" });
                            }}
                            placeholder="Email"
                        />
                        {error.email && (
                            <span style={{ color: "red", fontSize: "12px" }}>{error.email}</span>
                        )}

                    </Colxx>
                    <Colxx sm={12}>
                        <Label className="mt-2">Phone Number</Label>
                        <InputGroup>
                            <InputGroupAddon addonType="prepend">
                                <Input
                                    type="select"
                                    value={countryCode}
                                    onChange={(e) => {
                                        const newCode = e.target.value;
                                        setCountryCode(newCode);
                                        setPhone(`${newCode}${rawPhone}`);
                                    }}
                                    style={{
                                        width: '125px',
                                        textAlign: 'center',
                                        cursor: 'pointer',
                                    }}
                                >
                                    {countryCodes.map((c) => (
                                        <option key={c.code} value={c.dial_code}>
                                            {c.code} {c.dial_code} ({c.name})
                                        </option>
                                    ))}
                                </Input>
                            </InputGroupAddon>

                            <Input
                                type="text"
                                value={rawPhone}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    setRawPhone(val);
                                    setPhone(`${countryCode}${val}`);
                                }}
                                placeholder="Phone number"
                            />
                        </InputGroup>
                    </Colxx>
                </Row>
            </ModalBody>
            <ModalFooter>
                <Button color="primary" size="xs" onClick={handleSave} disabled={isSaving}>
                    Save
                </Button>
                <Button color="secondary" size="xs" outline onClick={toggle}>
                    Cancel
                </Button>
            </ModalFooter>
        </Modal>
    );
}
